import { z } from 'zod';
import {
  EntityIdSchema,
  HhMmSchema,
  IsoDateSchema,
  IsoDateTimeSchema,
  PaginatedResponseSchema,
} from './common';
import { ParticipanteSchema } from './participante';

/**
 * Estados posibles de una cohorte (ver docs/00-compartido/esquemas-datos.md §2).
 */
export const CohorteEstadoSchema = z.enum(['planificada', 'activa', 'cerrada', 'cancelada']);
export type CohorteEstado = z.infer<typeof CohorteEstadoSchema>;

/**
 * Rango de trimestre asociado a una cohorte.
 */
export const RangoTrimestreSchema = z.object({
  id: EntityIdSchema,
  cohorte_id: EntityIdSchema,
  nombre: z.string(),
  desde: IsoDateSchema,
  hasta: IsoDateSchema,
});
export type RangoTrimestre = z.infer<typeof RangoTrimestreSchema>;

/**
 * Sesión tentativa al crear una cohorte.
 *
 * Permite predefinir la lista de encuentros del programa (asociados a las
 * unidades curriculares) en el mismo paso que la creación de la cohorte.
 * Las fechas deben caer dentro del rango `fecha_inicio`–`fecha_fin` (lo
 * valida el servicio antes de persistir).
 */
export const SesionTentativaInputSchema = z.object({
  fecha: IsoDateSchema,
  hora_inicio: HhMmSchema,
  hora_fin: HhMmSchema,
  tema: z.string().min(1).optional(),
  unidad_curricular_id: EntityIdSchema.optional(),
}).superRefine((value, ctx) => {
  if (value.hora_fin <= value.hora_inicio) {
    ctx.addIssue({ code: 'custom', message: 'hora_fin debe ser > hora_inicio', path: ['hora_fin'] });
  }
});
export type SesionTentativaInput = z.infer<typeof SesionTentativaInputSchema>;

/**
 * Cohorte (edición concreta de un programa).
 */
export const CohorteSchema = z.object({
  id: EntityIdSchema,
  nombre: z.string().nullable().optional(),
  programa_id: EntityIdSchema,
  fecha_inicio: IsoDateSchema,
  fecha_fin: IsoDateSchema,
  estado: CohorteEstadoSchema,
  clave_aes_id: z.string(),
  created_at: IsoDateTimeSchema,
  // `usuario.id` NO es siempre un UUID: hay ids heredados de 32 caracteres sin
  // guiones (p. ej. el de `support@faugust.com`). Por eso esta FK no puede
  // usar `EntityIdSchema`: si el creador de la cohorte tiene esa forma de id,
  // el parseo de `GET /cohortes` fallaria y dejaria la pagina de cohortes rota.
  created_by_usuario_id: z.string().nullable().optional(),
});
export type Cohorte = z.infer<typeof CohorteSchema>;

/**
 * Input para POST /api/v1/cohortes.
 * NOTA: el backend NO valida formato UUID de programa_id (lo trata como string opaco).
 */
export const RangoTrimestreInputSchema = z
  .object({
    nombre: z.string().min(1),
    desde: IsoDateSchema,
    hasta: IsoDateSchema,
  })
  .superRefine((value, ctx) => {
    if (value.hasta < value.desde) {
      ctx.addIssue({ code: 'custom', message: 'hasta debe ser >= desde', path: ['hasta'] });
    }
  });

export const CohorteCreateInputSchema = z
  .object({
    nombre: z.string().optional(),
    programa_id: z.string().min(1),
    fecha_inicio: IsoDateSchema,
    fecha_fin: IsoDateSchema,
    rango_trimestres: z.array(RangoTrimestreInputSchema).min(1),
    /**
     * Sesiones tentativas predefinidas al crear la cohorte. Opcional.
     * Cada elemento referencia la unidad curricular que se imparte (si
     * aplica) y debe caer dentro del rango `fecha_inicio`/`fecha_fin`.
     */
    sesiones_tentativas: z.array(SesionTentativaInputSchema).optional(),
  })
  .superRefine((value, ctx) => {
    if (value.fecha_fin < value.fecha_inicio) {
      ctx.addIssue({ code: 'custom', message: 'fecha_fin debe ser >= fecha_inicio', path: ['fecha_fin'] });
    }
    if (value.sesiones_tentativas) {
      for (let i = 0; i < value.sesiones_tentativas.length; i++) {
        const s = value.sesiones_tentativas[i];
        if (s.fecha < value.fecha_inicio || s.fecha > value.fecha_fin) {
          ctx.addIssue({
            code: 'custom',
            message: `La fecha de la sesión #${i + 1} debe estar dentro de [${value.fecha_inicio}, ${value.fecha_fin}]`,
            path: ['sesiones_tentativas', i, 'fecha'],
          });
        }
      }
    }
  });
export type CohorteCreateInput = z.infer<typeof CohorteCreateInputSchema>;

/**
 * Input base para PATCH /api/v1/cohortes/:id (sin programa_id ni rango_trimestres, todos opcionales).
 */
const CohorteUpdateBaseSchema = z.object({
  nombre: z.string().optional(),
  fecha_inicio: IsoDateSchema,
  fecha_fin: IsoDateSchema,
});
export const CohorteUpdateInputSchema = CohorteUpdateBaseSchema.partial();
export type CohorteUpdateInput = z.infer<typeof CohorteUpdateInputSchema>;

/**
 * Query string de GET /api/v1/cohortes (filtros disponibles en backend).
 */
export const CohorteListQuerySchema = z.object({
  programa_id: z.string().optional(),
  estado: CohorteEstadoSchema.optional(),
});
export type CohorteListQuery = z.infer<typeof CohorteListQuerySchema>;

/**
 * Response de GET /api/v1/cohortes.
 */
export const CohorteListResponseSchema = PaginatedResponseSchema(CohorteSchema);
export type CohorteListResponse = z.infer<typeof CohorteListResponseSchema>;

/**
 * Response de GET /api/v1/cohortes/:id/participantes.
 */
export const CohorteParticipantesListResponseSchema = z.object({
  data: z.array(ParticipanteSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().min(1),
  page_size: z.number().int().min(1),
});
export type CohorteParticipantesListResponse = z.infer<typeof CohorteParticipantesListResponseSchema>;